import {
  centerMarkerRelativeToSwipeableDrawer,
  getBackToListView,
  gMapLatLngForMloc
} from 'Src/map-search-v9/lib/utils';
import {
  CLOSE_PROVIDER_CARD,
  ENABLE_SEARCH_THIS_AREA,
  LOCATION_PIN_CLICK,
  OPEN_SORT_AND_FILTER,
  SEARCH_THIS_AREA
} from 'Src/machines/map-search-v9/map-search-actions';
import { transformLocationsForGoogleMap } from 'Src/search-v9/utils/mapLocationTransformers';
import NavigationButtons from 'Src/map-search-v9/components/NavigationButtons';
import {
  getMapCenter,
  getMapContainerBoundingBox
} from 'Src/utils/map-operations';
import {
  NO_RESULTS,
  PROVIDER_CARD_OPEN,
  SORT_AND_FILTER_DRAWER_OPEN
} from 'Src/machines/map-search-v9/map-search-states';
import {
  DynamicGmap,
  getMarkerIconForState as getMarkerIconForGmaps,
  MarkerState as GmapMarkerStates,
  resetMarkersState,
  ZINDEXES
} from '@kyruus/gmap';
import { AwsMap } from '@kyruus/aws-map';
import { RENDER_PROVIDERS } from 'Src/machines/map-search/map-search-states';
import React, { useCallback, useRef } from 'react';

import { SwipeableProviderCardList } from 'Src/map-search-v9/components/SmallMediumView/SwipeableProviderCardList';
import { MAP_PROVIDERS } from 'Src/location-v9/Location/constants';
import { getMarkerIconForState as getMarkerIconForAws } from '@kyruus/aws-map/aws-map/dist/lib/utils';
import { MarkerState as AwsMarkerState } from '@kyruus/aws-map/aws-map/dist/lib/constants';
import { useMapQueryParams } from 'Src/search-v9/components/hooks/useMapQueryParams';
import { isProviderListMapsEnabled } from 'Common/config';
import { NoResultsInSearchArea } from 'Src/map-search/components/ErrorBanner';
import { defineMessages } from '@kyruus/intl';

// query params to omit when building the 'back to search' link.
// includes map-search specific deep linking params
const OMIT_QUERY_PARAMS_BACK_TO_SEARCH = ['location_id'];

const NORTH_LATITUDE_BOUND = 85;
const SOUTH_LATITUDE_BOUND = -85;
const EAST_LONGITUDE_BOUND = 180;
const WEST_LONGITUDE_BOUND = -180;

const messagesForBanner = defineMessages({
  noResultsBanner: {
    id: 'banner.noProvidersFoundInArea',
    description: 'Banner displayed when no providers are found in an area',
    defaultMessage:
      'We are unable to locate any providers based on your search area. Try searching in a different area.'
  }
});

export function SmallMediumProvidersMapView({
  isScreenMediumOrBelow,
  send,
  streetViewMapEvents,
  config,
  appSettings,
  state,
  history,
  log,
  isStreetViewOpen,
  location,
  match,
  getUpdatedSearch
}) {
  const { selectedLocation, providers, searchThisAreaEnabled } = state.context;

  const mapProvider = config?.maps_config?.location_service_provider;
  const useAwsMap = mapProvider === MAP_PROVIDERS.AWS;
  const { mapZoom, mapCenter } = useMapQueryParams({ location });

  const isAdvancedMarkersEnabled =
    config?.darkship_feature_flags?.enable_advanced_markers_for_maps;
  const isSearchThisAreaEnabled =
    config?.darkship_feature_flags?.map_show_search_this_area_button;

  const { search: currentQuery = '' } = location;

  const gmapRef = useRef(null);
  const markersRef = useRef(null);
  const awsMapRef = useRef(null);

  const backToSearchUrl = getBackToListView(
    currentQuery,
    OMIT_QUERY_PARAMS_BACK_TO_SEARCH
  );

  const searchSummary = {
    query_string: currentQuery
  };

  const mapLocations =
    (providers?._result || []) &&
    transformLocationsForGoogleMap(
      providers?._result || [],
      isAdvancedMarkersEnabled
    );

  const findLocationById = useCallback(
    (locationId) => {
      if (!providers._result || !locationId) return null;

      for (const provider of providers._result) {
        if (provider.locations) {
          const location = provider.locations.find(
            (loc) => loc.id === locationId
          );
          if (location) return location;
        }
      }

      return null;
    },
    [providers]
  );

  const onPinClick = (e) => {
    const {
      marker: { map },
      meta: { location }
    } = e;
    gmapRef.current = map;

    const locationId = location.id;

    const fullLocation = findLocationById(locationId) || location;
    if (log) {
      log('user_action.search_page.map.location_pin_click', {
        locationId,
        locationName: fullLocation.name || 'Unknown'
      });
    }
    send({
      type: LOCATION_PIN_CLICK,
      data: location
    });
  };

  const handleSwipeableDrawerClose = () => {
    send({
      type: CLOSE_PROVIDER_CARD
    });

    const selectedLocationId = selectedLocation.id;
    const markers = markersRef.current;
    const markerData = markers.find(
      ({ meta }) => meta.location.id === selectedLocationId
    );
    if (awsMapRef.current) {
      const { marker, meta } = markerData || {};
      meta.isFocused = false;
      meta.isClicked = false;
      const markerElement = marker?.getElement?.();
      if (markerElement) {
        markerElement.src = getMarkerIconForAws(AwsMarkerState.DEFAULT, {
          count: markerData?.meta?.location?.providerCount || 1
        });
        markerElement.style.zIndex = ZINDEXES.DEFAULT;
        markerElement.focus();
      }
    } else {
      const isAdvancedMarkersEnabled =
        config?.darkship_feature_flags?.enable_advanced_markers_for_maps;
      markerData.meta.isSelected = false;
      resetMarkersState({
        marker: markerData.marker,
        meta: markerData.meta,
        gMaps: window.google.maps,
        theme: config.theme,
        enableAdvancedMarkers: isAdvancedMarkersEnabled
      });
      if (
        markerData.marker &&
        typeof markerData.marker.getUnderlyingMarker === 'function'
      ) {
        markerData.marker.getUnderlyingMarker().focus();
      }
    }
  };

  const onSwipeableDrawerOpen = (ref) => {
    const cardHeight = ref.current.children[2].getBoundingClientRect().height; // TODO: is there a better way to access the sheet content ?

    const markers = markersRef.current;
    const selectedMarker = markers.find(({ meta }) => {
      return meta.location.id === selectedLocation.id;
    });
    if (!selectedMarker) {
      return;
    }
    const viewportCenter = window.innerHeight / 2;
    const panUpDistance = viewportCenter - cardHeight;

    if (awsMapRef.current) {
      const cardElement = ref.current.children[2];

      centerMarkerRelativeToSwipeableDrawer(
        awsMapRef,
        selectedMarker.marker,
        cardElement
      );
    } else {
      const markerPosition = selectedMarker.marker.position;
      gmapRef.current.panTo(markerPosition);
      gmapRef.current.panBy(0, -panUpDistance);
    }
  };
  const showMapInProviderList = isProviderListMapsEnabled(config);

  const shouldShowNoResultsBanner =
    showMapInProviderList &&
    state.matches(NO_RESULTS) &&
    state.context.searchParams.geo_bounding_box;

  const enableSearchThisArea = () => {
    send({
      type: ENABLE_SEARCH_THIS_AREA,
      data: true
    });
  };

  const mapOptions = {};
  if (mapZoom) {
    mapOptions.zoom = mapZoom;
  }
  if (mapCenter) {
    mapOptions.center = mapCenter;
  }

  return (
    <>
      {!isStreetViewOpen && (
        <NavigationButtons
          backToSearchUrl={backToSearchUrl}
          onSearchAreaClick={() => {
            const boundingBox = getMapContainerBoundingBox(
              mapProvider,
              mapProvider === MAP_PROVIDERS.GOOGLE ? gmapRef : awsMapRef
            );
            const center = getMapCenter(
              mapProvider,
              mapProvider === MAP_PROVIDERS.GOOGLE ? gmapRef : awsMapRef
            );
            const zoom =
              mapProvider === MAP_PROVIDERS.GOOGLE
                ? gmapRef.current.getZoom()
                : awsMapRef.current.getZoom();
            send({
              type: SEARCH_THIS_AREA,
              data: {
                geo_bounding_box: boundingBox,
                map_zoom_level: zoom,
                map_center: `${center.lat},${center.lng}`
              }
            });

            const query = getUpdatedSearch([
              {
                action: 'append',
                key: 'geo_bounding_box',
                value: boundingBox
              },
              {
                action: 'append',
                key: 'map_zoom_level',
                value: zoom
              },
              {
                action: 'append',
                key: 'map_center',
                value: `${center.lat},${center.lng}`
              }
            ]);

            history.push(query);

            if (log) {
              log('user_action.search_page.map.search_this_area_button_click');
            }
          }}
          onFiltersClick={() => {
            send({ type: OPEN_SORT_AND_FILTER });
            if (gmapRef.current && selectedLocation) {
              // recenter map
              gmapRef.current.panTo(gMapLatLngForMloc(selectedLocation));
              // pan by half the width of the filter drawer (20vw / 2 = 0.1)
              gmapRef.current.panBy(window.innerWidth * 0.1, 0);
            }
          }}
          history={history}
          log={log}
          isFiltersDisabled={
            state.matches(SORT_AND_FILTER_DRAWER_OPEN) ||
            shouldShowNoResultsBanner
          }
          showSearchThisArea={isSearchThisAreaEnabled && searchThisAreaEnabled}
        />
      )}

      {shouldShowNoResultsBanner && (
        <NoResultsInSearchArea messages={messagesForBanner} log={log} />
      )}
      {!useAwsMap ? (
        <DynamicGmap
          // re-render map when viewport has changed to re-bind event handlers
          key={isScreenMediumOrBelow ? 'medOrBelow' : 'large'}
          locations={mapLocations}
          enabledLocationIds={new Set(mapLocations.map((loc) => loc.id))}
          selectedLocation={selectedLocation}
          mapEventsToRegister={{
            dragend: (mapRef) => {
              if (mapRef.current?.getCenter()) {
                const lat = mapRef.current.getCenter().lat();
                const lng = mapRef.current.getCenter().lng();
                const zoom = mapRef.current.zoom;
                /* eslint-disable-next-line no-console */
                console.log('map.dragend ', { lat, lng, zoom });
              }
            },
            zoom_changed: (mapRef) => {
              if (mapRef.current?.getCenter()) {
                const lat = mapRef.current.getCenter().lat();
                const lng = mapRef.current.getCenter().lng();
                const zoom = mapRef.current.zoom;
                /* eslint-disable-next-line no-console */
                console.log('map.zoom_changed ', { lat, lng, zoom });
                enableSearchThisArea();
              }
            },
            idle: (mapRef) => {
              if (!gmapRef.current) {
                gmapRef.current = mapRef.current;
              }
            },
            center_changed: () => {
              if (gmapRef.current) {
                enableSearchThisArea();
              }
            },
            ...streetViewMapEvents
            // disabled for V1 of map search https://kyruus.jira.com/browse/KENG-29840
            // bounds_changed: onBoundsChanged
          }}
          shouldLabelMarkers={false}
          onMarkerClick={onPinClick}
          getMarkers={(markers) => {
            markersRef.current = markers;
          }}
          mapOptions={{
            maxZoom: 16,
            fullscreenControl: false,
            mapTypeControl: false,
            styles: [
              {
                featureType: 'poi',
                stylers: [{ visibility: 'off' }]
              }
            ],
            restriction: {
              // map restrictions
              latLngBounds: {
                north: NORTH_LATITUDE_BOUND,
                south: SOUTH_LATITUDE_BOUND,
                west: WEST_LONGITUDE_BOUND,
                east: EAST_LONGITUDE_BOUND
              },
              strictBounds: true
            },
            ...mapOptions
          }}
          theme={config.theme}
          enableAdvancedMarkers={isAdvancedMarkersEnabled}
          defaultMarkerIcon={getMarkerIconForGmaps(GmapMarkerStates.DEFAULT)}
          showMultipleMarkersForSameLocation={true}
        />
      ) : (
        <AwsMap
          awsMapConfig={{
            identityPoolId: appSettings.AWS_MAPS_CREDENTIALS.mapIdentityPoolId,
            mapName: appSettings.AWS_MAPS_CREDENTIALS.mapName,
            region: appSettings.AWS_MAPS_CREDENTIALS.mapRegion
          }}
          locations={mapLocations}
          getMarkers={(markers) => {
            markersRef.current = markers;
          }}
          mapOptions={{
            ...mapOptions
          }}
          onMarkerClick={onPinClick}
          mapEventsToRegister={{
            load: (mapInstance) => {
              if (!awsMapRef.current) {
                awsMapRef.current = mapInstance.target;
              }
            },
            dragstart: () => {
              if (!state.matches(`${PROVIDER_CARD_OPEN}.${RENDER_PROVIDERS}`)) {
                send({
                  type: ENABLE_SEARCH_THIS_AREA,
                  data: true
                });
              }
            },
            zoom: () => {
              if (!state.matches(`${PROVIDER_CARD_OPEN}.${RENDER_PROVIDERS}`)) {
                send({
                  type: ENABLE_SEARCH_THIS_AREA,
                  data: true
                });
              }
            }
          }}
          showFullscreenControl={false}
          enableAdvancedMarkers={isAdvancedMarkersEnabled}
        />
      )}

      <SwipeableProviderCardList
        config={config}
        location={location}
        searchSummary={searchSummary}
        send={send}
        state={state}
        markersRef={markersRef}
        awsMapRef={awsMapRef}
        gmapRef={gmapRef}
        handleClose={handleSwipeableDrawerClose}
        onOpen={onSwipeableDrawerOpen}
        log={log}
        match={match}
      />
    </>
  );
}
